import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Mic, MessageSquare, Sparkles } from "lucide-react";
import { formatProMonthlyPrice } from "@/lib/pricing";
import { UpgradeButton } from "./UpgradeButton";

export interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'voice' | 'chat' | 'situation';
  used?: number;
  limit?: number;
}

const PAYWALL_CONTENT = {
  voice: {
    title: "Assine para falar 🎤",
    subtitle: `A prática por voz entra na assinatura de ${formatProMonthlyPrice()}/mês. Conversas liberadas, sem cota.`,
    icon: Mic,
    benefits: [
      "Conversas liberadas, sem cota",
      "Catálogo inteiro por nível",
      "Feedback depois de cada prática",
    ],
  },
  chat: {
    title: "Chat faz parte da assinatura 💬",
    subtitle: "O Talki é só por assinatura. Com ela, as conversas ficam liberadas.",
    icon: MessageSquare,
    benefits: [
      "Conversas liberadas, sem cota",
      "Catálogo inteiro por nível",
      "Feedback depois de cada prática",
    ],
  },
  situation: {
    title: "Assine para desbloquear ✨",
    subtitle: "O Talki é só por assinatura.",
    icon: Sparkles,
    benefits: [
      "Conversas liberadas, sem cota",
      "Catálogo inteiro por nível",
      "Histórico inteiro",
    ],
  },
};

export function PaywallModal({ isOpen, onClose, type }: PaywallModalProps) {
  const content = PAYWALL_CONTENT[type];
  const Icon = content.icon;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center space-y-4 pt-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
            <Icon className="h-8 w-8 text-blue-600" />
          </div>
          <DialogTitle className="text-xl md:text-2xl font-bold">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            {content.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Com a assinatura você tem:
            </p>
            <ul className="space-y-3">
              {content.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-success" />
                  </div>
                  <span className="text-sm md:text-base">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center p-4 rounded-2xl border border-neutral-200 bg-neutral-50">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-3xl font-bold text-foreground">{formatProMonthlyPrice()}</span>
              <span className="text-muted-foreground">/mês</span>
            </div>
          </div>

          <div className="space-y-3">
            <UpgradeButton
              className="w-full min-h-[52px] rounded-full text-base font-semibold"
              size="lg"
            >
              Assinar agora
            </UpgradeButton>
            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full min-h-[44px] text-muted-foreground hover:text-foreground"
            >
              Agora não
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
